// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSlice } from '@reduxjs/toolkit';
import {
  DeleteProjectsFailedAction,
  DeleteProjectsStartAction,
  DeleteProjectsSuccessAction,
  GetMapCoordinatesFailedAction,
  MapCoordinatesFetchDroppedAction,
  InvalidateMapCoordinatesAction,
  GetMapCoordinatesStartAction,
  GetMapCoordinatesSuccessAction,
  GetProjectsFailedAction,
  GetProjectsStartAction,
  GetProjectsSuccessAction,
  MarkOrderSortFilterTouchedAction,
  SetKeywordFilterAction,
  SetShowMapviewAction,
  SetSelectedPinProjectAction,
  SetPinGroupAction,
  SetMapFocusAction,
  SetEsriCameraAction,
  SetRailViewAction,
  SetLoadAllAction,
  SetLoadNewAction,
  SetOrderSortFilterAction,
  SetPageAction,
  SetProjectsAction,
  SetSortFilterAction,
  SetThumbnailAction,
  UpdateProjectNameAction,
  DeleteProjectListLasFileAction, DeleteProjectListTifFileAction,
  UpdateProjectListLasFileAction, UpdateProjectListTifFileAction, UpdateProjectListImageryRequestSuccessAction,
} from './projectListActions';

const initialState: IProjectListState = {
  projectsLoading: false,
  projectsCount: 0,
  loadAll: false,
  loadNew: true,
  page: 0,
  list: [],
  keywordFilter: '',
  sortFilter: 'updatedAtD',
  orderSortFilter: 'unassigned',
  orderSortFilterTouched: false,
  mapCoordinates: [],
  mapCoordinatesLoading: false,
  mapCoordinatesKey: '',
  mapMissingLocationCount: 0,
  showMapview: false,
  selectedPinProjectId: null,
  selectedPinGroupIds: null,
  mapFocusTarget: null,
  esriCamera: null,
  railView: null,
};

const projectListSlice = createSlice({
  name: 'projectList',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(SetSortFilterAction, (state, action) => {
        state.sortFilter = action.payload;
      })
      .addCase(SetKeywordFilterAction, (state, action) => {
        state.keywordFilter = action.payload;
      })
      .addCase(SetOrderSortFilterAction, (state, action) => {
        state.orderSortFilter = action.payload;
      })
      .addCase(MarkOrderSortFilterTouchedAction, (state) => {
        state.orderSortFilterTouched = true;
      })
      .addCase(SetPageAction, (state, action) => {
        state.page = action.payload;
      })
      .addCase(GetProjectsStartAction, (state) => {
        state.projectsLoading = true;
      })
      .addCase(GetProjectsSuccessAction, (state, action) => {
        state.projectsLoading = false;
        state.list = [...state.list, ...action.payload.list];

        state.projectsCount = action.payload.totalCount;
      })
      .addCase(GetProjectsFailedAction, (state) => {
        state.projectsLoading = false;
      })
      .addCase(GetMapCoordinatesStartAction, (state) => {
        state.mapCoordinatesLoading = true;
      })
      .addCase(GetMapCoordinatesSuccessAction, (state, action) => {
        state.mapCoordinatesLoading = false;
        // Keep thumbnails the rail already resolved: the server always sends the raw private
        // S3 URL, and letting it overwrite a resolved one visibly reverts mounted cards to
        // placeholders on every search or filter change.
        const resolvedById = new Map(
          state.mapCoordinates.filter((c) => c.thumbnailResolved).map((c) => [c._id, c.thumbnail]),
        );
        state.mapCoordinates = action.payload.coordinates.map((coordinate) => (
          resolvedById.has(coordinate._id)
            ? { ...coordinate, thumbnail: resolvedById.get(coordinate._id), thumbnailResolved: true }
            : coordinate
        ));
        state.mapMissingLocationCount = action.payload.missingLocationCount;
        state.mapCoordinatesKey = action.payload.requestKey || '';
        // A refetched set can drop members of an open co-located group (or the selection
        // itself); pruning here keeps the pager honest and the card from paging onto ghosts.
        if (state.selectedPinGroupIds) {
          const plotted = new Set(action.payload.coordinates.map((coordinate) => coordinate._id));
          const surviving = state.selectedPinGroupIds.filter((id) => plotted.has(id));
          state.selectedPinGroupIds = surviving.length > 1 ? surviving : null;
        }
        if (state.selectedPinProjectId
          && !action.payload.coordinates.some((coordinate) => coordinate._id === state.selectedPinProjectId)) {
          state.selectedPinProjectId = null;
          state.selectedPinGroupIds = null;
        }
      })
      .addCase(MapCoordinatesFetchDroppedAction, (state) => {
        // A superseded fetch was dropped and the current key is already answered - nothing
        // is loading anymore.
        state.mapCoordinatesLoading = false;
      })
      .addCase(InvalidateMapCoordinatesAction, (state) => {
        // Data changed; the cached set may keep rendering, but the next open must refetch.
        state.mapCoordinatesKey = '';
      })
      .addCase(GetMapCoordinatesFailedAction, (state) => {
        state.mapCoordinatesLoading = false;
        // A failed fetch must not leave the previous query's pins masquerading as results,
        // and must not claim its key - the next map-view open retries.
        state.mapCoordinates = [];
        state.mapMissingLocationCount = 0;
        state.mapCoordinatesKey = '';
      })
      .addCase(SetShowMapviewAction, (state, action) => {
        state.showMapview = action.payload;
      })
      .addCase(SetSelectedPinProjectAction, (state, action) => {
        state.selectedPinProjectId = action.payload;
        // Selecting outside the co-located group (another pin, a rail card, deselection)
        // dissolves it; selecting within it is the card's pager at work.
        if (!action.payload || !state.selectedPinGroupIds?.includes(action.payload)) {
          state.selectedPinGroupIds = null;
        }
      })
      .addCase(SetPinGroupAction, (state, action) => {
        state.selectedPinGroupIds = action.payload.ids;
        state.selectedPinProjectId = action.payload.ids[0] || null;
      })
      .addCase(SetMapFocusAction, (state, action) => {
        state.mapFocusTarget = { ...action.payload, seq: (state.mapFocusTarget?.seq || 0) + 1 };
      })
      .addCase(SetRailViewAction, (state, action) => {
        state.railView = action.payload;
      })
      .addCase(SetEsriCameraAction, (state, action) => {
        state.esriCamera = action.payload;
      })
      // The project need not be in list: that holds one page, while a delete can be started from
      // the folder page or from any card further down the grid. Throwing here aborted the thunk
      // before it issued the request, so the dialog sat on its spinner and nothing was deleted.
      .addCase(DeleteProjectsStartAction, (state, action) => {
        const listed = state.list.find((p) => p._id === action.payload);
        if (listed) listed.deleting = true;
      })
      .addCase(DeleteProjectsSuccessAction, (state, action) => {
        state.list = state.list.filter((p) => p._id !== action.payload);
        // The map/rail read mapCoordinates - a deleted project must drop its pin and card,
        // leave any co-located group it was in, correct the without-location badge, and
        // invalidate the cache key so the next map-view open refetches.
        const deleted = state.mapCoordinates.find((c) => c._id === action.payload);
        if (deleted && !deleted.middlePoint && state.mapMissingLocationCount > 0) {
          state.mapMissingLocationCount -= 1;
        }
        state.mapCoordinates = state.mapCoordinates.filter((c) => c._id !== action.payload);
        if (state.selectedPinGroupIds) {
          const surviving = state.selectedPinGroupIds.filter((id) => id !== action.payload);
          state.selectedPinGroupIds = surviving.length > 1 ? surviving : null;
        }
        if (state.selectedPinProjectId === action.payload) state.selectedPinProjectId = null;
        state.mapCoordinatesKey = '';
      })
      .addCase(DeleteProjectsFailedAction, (state, action) => {
        const listed = state.list.find((p) => p._id === action.payload);
        if (listed) listed.deleting = false;
      })
      .addCase(SetProjectsAction, (state, action) => {
        state.list = action.payload;
      })
      .addCase(SetLoadAllAction, (state, action) => {
        state.projectsLoading = false;
        state.loadAll = action.payload;
      })
      .addCase(SetLoadNewAction, (state, action) => {
        state.loadNew = action.payload;
      })
      .addCase(SetThumbnailAction, (state, action) => {
        const index = state.list.findIndex((p) => p._id === action.payload.projectId);
        if (index > -1) {
          state.list[index].thumbnail = action.payload.thumbnail;
        }
        // The map rail renders from mapCoordinates, so a resolved thumbnail reaches it too;
        // the resolved flag lets a coordinates refetch keep it instead of re-serving the raw URL.
        const coordinate = state.mapCoordinates.find((p) => p._id === action.payload.projectId);
        if (coordinate) {
          coordinate.thumbnail = action.payload.thumbnail;
          coordinate.thumbnailResolved = true;
        }
      })
      // Applied to whichever collections hold the project, and to each independently. list holds
      // one page while mapCoordinates holds every match, so a project can easily be in the second
      // and not the first - and the map's copy has to be updated either way, or the pin and rail
      // card keep showing the old name until a search or refresh happens to refetch them.
      .addCase(UpdateProjectNameAction, (state, action) => {
        const listed = state.list.find((p) => p._id === action.payload._id);

        // An ops trainer edits the stand-in name their view shows in place of the real one;
        // everyone else edits the name itself. opsTrainer says who is editing, not whether this
        // project carries a training subdocument - the server sends training only for projects
        // in training, so a trainer renaming an ordinary project arrives here without it.
        //
        // Only the list is touched for the stand-in: the map and rail render name, never
        // nameEditable, and the coordinates payload does not carry it.
        if (action.payload.opsTrainer) {
          const nameEditable = action.payload.training?.nameEditable;
          if (listed && nameEditable !== undefined) listed.training = { ...listed.training, nameEditable };
          return;
        }

        if (listed) listed.name = action.payload.name;
        // Looked up only on this branch - mapCoordinates is the complete set, unbounded where
        // list is one page, so there is no reason to walk it for a rename that cannot use it.
        const coordinate = state.mapCoordinates.find((c) => c._id === action.payload._id);
        if (coordinate) coordinate.name = action.payload.name;
      })
      .addCase(UpdateProjectListTifFileAction, (state, action) => {
        const projectInList = state.list.find((p) => p._id === action.payload.ownerProject);
        if (projectInList) {
          const index = projectInList.tifFiles.findIndex((t) => t._id === action.payload._id);
          if (index < 0) {
            projectInList.tifFiles.push(action.payload);
          } else {
            projectInList.tifFiles[index] = action.payload;
          }
        }
      })
      .addCase(UpdateProjectListLasFileAction, (state, action) => {
        const projectInList = state.list.find((p) => p._id === action.payload.ownerProject);
        if (projectInList) {
          const index = projectInList.lasFiles.findIndex((t) => t._id === action.payload._id);
          if (index < 0) {
            projectInList.lasFiles.push(action.payload);
          } else {
            projectInList.lasFiles[index] = action.payload;
          }
        }
      })
      .addCase(DeleteProjectListTifFileAction, (state, action) => {
        const projectInList = state.list.find((p) => p._id === action.payload.projectId);
        if (projectInList) {
          projectInList.tifFiles = projectInList.tifFiles.filter((t) => t._id !== action.payload.tifFileId);
        }
      })
      .addCase(DeleteProjectListLasFileAction, (state, action) => {
        const projectInList = state.list.find((p) => p._id === action.payload.projectId);
        if (projectInList) {
          projectInList.lasFiles = projectInList.lasFiles.filter((t) => t._id !== action.payload.lasFileId);
        }
      })
      .addCase(UpdateProjectListImageryRequestSuccessAction, (state, action) => {
        const projectInList = state.list.find((p) => p._id === action.payload.ownerProject);
        if (projectInList) {
          projectInList.imageryRequest = action.payload;
        }
      });
  },
});

export const { reducer } = projectListSlice;

export default reducer;
